import { useEffect, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";

import { buildNavigation, canOpen, dispatchConsolePages, pagesNotInRole } from "../navigation.ts";
import type { Session } from "../session.ts";
import { HeaderKit } from "./HeaderKit.tsx";

/**
 * Tips shown in the help popover.
 *
 * These are the shell tips the design file itself passes, and they are the shell tips for every page
 * because no per-page text exists: SCREEN_SPEC names the help popover on every console and supplies no
 * words for it. Recorded as Q-03 rather than written here, since inventing page tips is inventing
 * product copy.
 */
const SHELL_TIPS = [
  "Menu groups fold with the chevron; the button beside the logo collapses the whole menu.",
  "The tenant chip always names whose data you are seeing.",
  "Hidden pages are counted at the bottom of the menu, with the permission they need.",
] as const;

/**
 * Whether a media query holds, as a value this component can read.
 *
 * The rail's width used to be a media query and nothing else, which made these tips a lie: the
 * first one describes a chevron and a button that did not exist, because a `@media` rule cannot be
 * clicked. The design computes the same thing from its frame width and then lets the operator
 * override it, so the width is the *default* and the operator's choice wins — which is a value in
 * JavaScript, not a breakpoint.
 *
 * Subscribed rather than read once: an operator who turns a tablet sideways crosses a breakpoint,
 * and a rail that keeps the old default until the next navigation is a rail that disagrees with the
 * page beside it.
 */
function useMedia(query: string, whenUnavailable: boolean): boolean {
  /*
   * `whenUnavailable` is required rather than defaulted, and it is not defensive padding.
   *
   * jsdom does not implement `matchMedia`, so the first version of this threw
   * `window.matchMedia is not a function` the moment any suite rendered the shell — and a `false`
   * fallback would have been worse than the crash: both queries answering false puts the rail in
   * its collapsed state, so every test rendering the chrome would have been quietly asserting
   * against a glyph rail. The honest fallback is the answer a browser with no media support would
   * give, which is the widest case: not a drawer, and a full rail. That is also the markup these
   * suites saw before the rail had a control at all, so the caller states it per query and the
   * default cannot be wrong by omission.
   */
  const supported = (): boolean =>
    typeof window !== "undefined" && typeof window.matchMedia === "function";
  const [holds, setHolds] = useState(() =>
    supported() ? window.matchMedia(query).matches : whenUnavailable,
  );
  useEffect(() => {
    if (!supported()) return undefined;
    const media = window.matchMedia(query);
    setHolds(media.matches);
    const listen = (event: MediaQueryListEvent): void => setHolds(event.matches);
    media.addEventListener("change", listen);
    return (): void => media.removeEventListener("change", listen);
  }, [query]);
  return holds;
}

/**
 * The console's chrome: who you are, which tenant, and what you can reach.
 *
 * Two rules govern everything here.
 *
 * The tenant is never implicit. A platform operator can cross the boundary, and
 * the cost of forgetting which side they are on is not a wrong screen — it is
 * cancelling a real trip belonging to a company they have never heard of. So it
 * is in the chrome at all times, and an actor who can cross tenants is told
 * that they can, because the danger belongs to them specifically.
 *
 * Navigation shows only what this operator can reach. 273 scopes exist and
 * nobody holds many of them; a sidebar of doors that mostly do not open teaches
 * an operator to distrust the whole thing within a shift.
 */

export interface ShellProps {
  readonly session: Session;
  readonly currentPageId: string;
  readonly onNavigate: (pageId: string) => void;
  /** Passed through to the header, whose notification bell reads NTF-05. */
  readonly transport: Transport;
  /**
   * What the account menu's last row does.
   *
   * Forwarded rather than handled here: the header draws the row, the authority ends the session, and
   * the shell is neither. Absent on the development path, where there is no session to end — and the
   * row is then inert rather than hidden, because the design's account menu has five rows and
   * removing one would be removing part of the header kit.
   */
  readonly onSignOut?: () => void;
  readonly children: ReactNode;
}

export function Shell({
  session,
  currentPageId,
  onNavigate,
  transport,
  onSignOut,
  children,
}: ShellProps): ReactNode {
  /*
   * The drawer, below 640.
   *
   * Design rule 1 requires it, and the six console design files place it at one of two widths. Four
   * of them — `17 WP16 Tenant Admin`, `18 WP17 Finance Console`, `19 WP18 Dispatch Operations` and
   * `20 WP19 Support Console` — drop the rail out of the layout below 640 and grow a menu button on
   * the top bar; `02 Console Shell v3` and `04 Dispatch Exceptions Board` do it at 900. The majority
   * is followed and the disagreement is recorded as Q-08, because between 640 and 900 the two
   * readings draw different screens: the majority still shows the 76px icon rail there.
   *
   * Which of the two is in force is a media query, so this is only whether the drawer is open —
   * above 640 nothing reads it.
   */
  const [navOpen, setNavOpen] = useState(false);

  /*
   * The drawer breakpoint and the rail's default width, as values rather than as rules.
   *
   * 640 and 1240 are the two the stylesheet already used, and they keep the same meanings: below
   * 640 the rail is a drawer, and 1240 is where the design stops defaulting to the glyph rail.
   */
  // Not a drawer and a full rail when `matchMedia` is unavailable: the widest case, which is
  // what a browser with no media support would show and what these suites rendered before the
  // rail had a control.
  const drawer = useMedia("(max-width: 639px)", false);
  const wideByDefault = useMedia("(min-width: 1240px)", true);

  /*
   * Whether the operator has collapsed the rail, and `null` until they say.
   *
   * `null` is not the same as `false` and the distinction is the whole behaviour: until the operator
   * touches the control the rail follows the width, and afterwards it follows them. The design does
   * exactly this — `sideCollapsed == null ? fw < 1240 : sideCollapsed` — and all seven console
   * design files draw the control that sets it. This build had no control at all, so the rail was a
   * function of the viewport and the operator could not have an opinion. That is the fault the
   * operator reported as "the foldable/collapsible menu" missing.
   *
   * In the drawer the rail is always full: an opened drawer showing glyphs without words would be
   * 260px of unexplained icons, and no design file draws it that way.
   */
  const [collapsed, setCollapsed] = useState<boolean | null>(null);
  const full = drawer ? true : !(collapsed ?? !wideByDefault);

  /*
   * Which menu groups the operator has folded.
   *
   * Folded groups are named rather than counted, so the set survives a navigation changing which
   * groups exist — an operator who folds Supply and then loses the Configuration group to a
   * permission change should still find Supply folded.
   *
   * Hidden in the collapsed rail, where the design hides the headings and shows every item: a
   * folded group in a glyph rail would be items missing with nothing on screen explaining why.
   */
  const [folded, setFolded] = useState<readonly string[]>([]);
  const isFolded = (section: string): boolean => full && folded.includes(section);

  const sections = buildNavigation(session.scopes);
  const hidden = pagesNotInRole(session.scopes);
  const page = dispatchConsolePages.find((candidate) => candidate.id === currentPageId);
  const reachable = canOpen(currentPageId, session.scopes);

  return (
    <div
      /*
       * The rail's width is a class now, not a breakpoint.
       *
       * It has to be: the operator can override the width's default, so no media query can know the
       * answer. `shell--rail` and `shell--full` are written after the old breakpoint rules in the
       * stylesheet and win on source order, and the breakpoints stay as the value these classes are
       * computed from rather than as a second opinion about the result.
       */
      className={
        `shell shell--${full ? "full" : "rail"}` +
        (session.crossTenant ? " shell--cross-tenant" : "")
      }
    >
      {/*
        No second bar. There was one: a dark strip carrying the product, the tenant and the operator,
        above the top bar the header kit draws — so the tenant was named twice on every page, the same
        duplication the breadcrumbs had and for the same reason, a part built from a rule before the
        screen was looked at.

        The reference screenshots have one bar. The tenant sits at its left, the help, bell and avatar
        at its right, and the two things this strip was carrying belong in the sidebar: the product at
        its head and the operator at its foot, which is where `Console Shell/01 Shell light.jpg` puts
        them.
      */}
      <nav
        className={navOpen ? "shell__nav shell__nav--open" : "shell__nav"}
        aria-label="Console sections"
      >
        {/*
          The product at the head of the rail, as the screenshots have it. The name leaves with the
          menu labels when the rail collapses to 76px, which is what `02 Console Shell v3` and the
          four work-package files all do: the mark stays, centred, and nothing else in the head
          survives the width.
        */}
        <div className="shell__brand">
          <span aria-hidden="true" className="shell__brand-mark">
            dashboard
          </span>
          <span className="shell__brand-name">Dispatch</span>
          {/*
            The button beside the logo that collapses the whole menu.

            All seven console design files draw it, with the same two labels and the same two glyphs
            — `Collapse menu` / `left_panel_close` and `Expand menu` / `left_panel_open` — and the
            same 30px square on `--navItem` with an 8px radius. Six of the seven also change the
            rail's width when it is clicked; `05 Dispatch Live Map` draws a 64px rail at every width
            and the control with it, which is a screen that never expands the rail rather than a
            dissent about the control.

            The label names what the click will do, not what the rail currently is. That is the
            design's own reading and it is also what makes the state measurable from outside: a
            harness can find the control by name and know which way it is pointing.

            In the drawer it closes the drawer, which is what `17`, `18`, `19` and `20` do with it —
            there is no narrower rail to go to, and the button is then the only thing in the drawer
            that dismisses it other than choosing a page.
          */}
          <button
            type="button"
            className="shell__rail-toggle"
            aria-label={full ? "Collapse menu" : "Expand menu"}
            title={full ? "Collapse menu" : "Expand menu"}
            /*
             * No `aria-expanded`, which this carried briefly. None of the seven design files puts
             * it here, and the label already says which way the control points — but the real
             * argument against it came from the harness: `aria-expanded` on a button inside the
             * rail is how a *group heading* is identified, so the toggle was counted as an
             * eleventh menu group and the collapsed rail reported one folded group it did not
             * have. Two things claiming the same ARIA role in the same container is a
             * mis-description, and it misled a reader built to trust it.
             */
            onClick={() => {
              if (drawer) {
                setNavOpen(false);
                return;
              }
              setCollapsed(full);
            }}
          >
            {/*
              Its own class rather than `.shell__nav-glyph`, which it carried for about a minute.
              The rail's existing suite counts glyphs and asserts one per page this operator can
              reach, and reusing the menu item's class made the toggle the eleventh item in a
              ten-page console. That assertion is the one that caught it, which is what it is for.
            */}
            <span aria-hidden="true" className="shell__rail-toggle-glyph">
              {full ? "left_panel_close" : "left_panel_open"}
            </span>
          </button>
        </div>

        {sections.length === 0 ? (
          // Not an empty sidebar. An operator with no reachable pages has been
          // given an account and no permissions, and telling them so is the
          // only useful thing this screen can do.
          <p className="shell__nav-empty">
            Your account has no dispatch permissions yet. Ask whoever administers your roles.
          </p>
        ) : (
          sections.map((section) => (
            <div key={section.section} className="shell__section">
              {/*
                The group heading is the fold control, which is how all seven design files build it:
                a button carrying `aria-expanded`, the group's name, and an `expand_more` chevron
                that rotates to -90° when the group is closed.

                It was an `h2` here, and that is why nothing folded. The heading was the right
                semantic for a list that could not be operated and the wrong one for a list that
                can; the design has no heading element at all, and a heading that cannot be clicked
                is not a quieter version of this control, it is the absence of it.

                `aria-expanded` is on the button rather than on the list for the same reason the
                design puts it there: it is the control's own state, and it is what tells both a
                screen reader and the fidelity harness which way the group is pointing.
              */}
              <button
                type="button"
                className="shell__section-head"
                aria-expanded={!isFolded(section.section)}
                onClick={() => {
                  setFolded((current) =>
                    current.includes(section.section)
                      ? current.filter((name) => name !== section.section)
                      : [...current, section.section],
                  );
                }}
              >
                <span className="shell__section-title">{section.section}</span>
                <span aria-hidden="true" className="shell__section-chevron">
                  expand_more
                </span>
              </button>
              <ul hidden={isFolded(section.section)}>
                {section.pages.map(({ page: entry, access }) => (
                  <li key={entry.id}>
                    <button
                      type="button"
                      aria-current={entry.id === currentPageId ? "page" : undefined}
                      /*
                       * The title is the design's own, and it is load-bearing rather than a nicety:
                       * in the 76px rail the label is `display: none`, which takes it out of the
                       * accessibility tree as well as off the screen, so the title is all that
                       * names the button. The design files set it on every menu item for the same
                       * reason.
                       */
                      title={entry.title}
                      onClick={() => {
                        onNavigate(entry.id);
                        // A drawer that stayed open over the page it just opened would hide it.
                        setNavOpen(false);
                      }}
                    >
                      <span aria-hidden="true" className="shell__nav-glyph">
                        {entry.glyph}
                      </span>
                      <span className="shell__nav-label">
                        {entry.title}
                        {access === "read_only" ? (
                          // Said in navigation rather than discovered when a
                          // button is missing. An operator who knows they are
                          // watching rather than acting escalates instead of
                          // hunting for a control that was never there.
                          <span className="shell__readonly"> · view only</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}

        {/*
          What this operator cannot see, counted rather than hidden. The design puts it at the foot
          of the rail, and it is the difference between a console that looks small and one that looks
          broken: an operator seeing two of ten pages is told there are more and what they cost.
        */}
        {hidden.length === 0 ? null : (
          <details className="shell__hidden">
            <summary>
              <span aria-hidden="true" className="shell__hidden-glyph">
                visibility_off
              </span>
              {hidden.length === 1
                ? "1 page not in your role"
                : `${hidden.length} pages not in your role`}
            </summary>
            <ul>
              {hidden.map((page) => (
                <li key={page.id}>
                  <span className="shell__hidden-title">{page.title}</span>
                  {page.needs.length === 0 ? null : (
                    <span className="shell__hidden-needs">{page.needs.join(" + ")}</span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        {/*
          The operator at the foot of the rail, as the screenshots have it: who you are, and what
          role you are acting in. The role is the tenant, because the token carries no role claim —
          the same absence recorded as Q-05 for the account panel, answered the same way rather than
          filled with something plausible.
        */}
        <div className="shell__operator">
          <span aria-hidden="true" className="shell__operator-avatar">
            {session.initials}
          </span>
          <span className="shell__operator-text">
            <span className="shell__operator-name">{session.displayName}</span>
            <span className="shell__operator-role">{session.tenantName}</span>
          </span>
        </div>
      </nav>

      {/*
        The scrim under an open drawer.

        `02 Console Shell v3` draws one — `showNavOverlay: drawer && navOpen` — and this build had
        none, so the drawer opened over a page that still looked and behaved as though it were the
        thing in front. Clicking it dismisses the drawer, which is the gesture a person tries first
        and which a drawer with no scrim gives them no way to make: until now the only ways out were
        choosing a page or finding the menu button again underneath the open rail.

        Inert to assistive technology rather than described: it is a dismiss affordance for a
        pointer, and the drawer itself is a labelled region a screen-reader user leaves by other
        means. A scrim announced as a button is a control with no name.
      */}
      {drawer && navOpen ? (
        <div
          className="shell__scrim"
          aria-hidden="true"
          onClick={() => {
            setNavOpen(false);
          }}
        />
      ) : null}

      <main className="shell__main">
        <HeaderKit
          session={session}
          helpTitle={page?.title ?? "Dispatch"}
          tips={SHELL_TIPS}
          transport={transport}
          navOpen={navOpen}
          {...(onSignOut === undefined ? {} : { onSignOut })}
          onToggleNav={() => {
            setNavOpen((open) => !open);
          }}
        />

        {page === undefined ? (
          <div className="state state--empty">
            <p className="state__headline">No such page</p>
          </div>
        ) : !reachable ? (
          <div className="state state--forbidden">
            <p className="state__headline">You do not have access to {page.title}</p>
            <p className="state__detail">
              {page.blockedBy ?? "Ask whoever administers your roles for access."}
            </p>
          </div>
        ) : (
          children
        )}
      </main>
    </div>
  );
}
